import {
  ATTR_NAMES,
  getAttrFromProduct,
} from '@hyphengroup/cms-components/types/attrNames';
import {
  formatAttributeItemValue,
  resolveProductTag,
} from '@hyphengroup/cms-components/utils/helpers';
import {
  getPaymentBadgeSources,
  PaymentBadgeSource,
} from '@hyphengroup/cms-components/utils/paymentBadgeIcon';
import { findMetadataProductCard } from '@hyphengroup/cms-components/utils/productMetaConfig';
import { BrandId } from 'utils/override';
import {
  BaseAttribute,
  CmsProduct,
  ComponentProductMetadata,
  SystemAttribute,
} from '$/(cms)/_modules/cmsService/types';

const SUPPORTED_PAYMENT_NETWORK_ATTRIBUTE = 'SUPPORTED_PAYMENT_NETWORK';
const SUPPORTED_PAYMENT_TYPE_ATTRIBUTE = 'SUPPORTED_PAYMENT_TYPE';
const OCTOPUS_AUTO_ADD_VALUE = 'Octopus Auto Add-Value';

export type ProductHeroKeyStat = {
  key: string;
  label: string;
  primaryValue: string;
  secondaryValue?: string;
};

export type ProductHeroData = {
  headline?: string;
  bodyCopy?: string;
  productTag?: string;
  promoCode?: string;
  paymentNetworks: PaymentBadgeSource[];
  paymentTypes: PaymentBadgeSource[];
  keyStats: ProductHeroKeyStat[];
};

type ProductAttributeIndex = {
  byName: Map<string, BaseAttribute>;
};

const isDefined = <T>(value: T | undefined): value is T => value !== undefined;

const normalizeText = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === '-') {
    return undefined;
  }

  return trimmed;
};

const createProductAttributeIndex = (
  product: CmsProduct | undefined,
): ProductAttributeIndex => {
  const byName = new Map<string, BaseAttribute>();

  for (const attribute of product?.attributes ?? []) {
    const attributeName = attribute.name?.name;

    if (attributeName && !byName.has(attributeName)) {
      byName.set(attributeName, attribute);
    }
  }

  return {
    byName,
  };
};

const getAttributeByName = (
  attributeIndex: ProductAttributeIndex,
  attributeName: string,
): BaseAttribute | undefined => attributeIndex.byName.get(attributeName);

const getAttributeBySelector = (
  attributeIndex: ProductAttributeIndex,
  selector?: SystemAttribute,
): BaseAttribute | undefined =>
  selector?.name ? attributeIndex.byName.get(selector.name) : undefined;

const getAttributeValue = (attribute?: BaseAttribute): string | undefined => {
  if (!attribute) {
    return undefined;
  }

  return normalizeText(formatAttributeItemValue(attribute, false, true));
};

const getAttributeLabel = (
  attribute: BaseAttribute | undefined,
  selector?: SystemAttribute,
): string | undefined =>
  normalizeText(attribute?.label) ??
  normalizeText(attribute?.name?.label) ??
  normalizeText(selector?.label) ??
  normalizeText(selector?.name);

const shouldSplitKeyStatValue = (value: string): boolean =>
  /^[\d$€¥₽£]/.test(value) || /^HKD\s|^SGD\s|^USD\s|^NTD\s/.test(value);

const splitKeyStatValue = (
  value: string,
): Pick<ProductHeroKeyStat, 'primaryValue' | 'secondaryValue'> => {
  if (!shouldSplitKeyStatValue(value)) {
    return {
      primaryValue: value,
    };
  }

  const [primaryValue, ...rest] = value.trim().split(/\s+/);
  const secondaryValue = normalizeText(rest.join(' '));

  return {
    primaryValue,
    secondaryValue,
  };
};

const isSelectionAttribute = (
  attribute?: BaseAttribute,
): attribute is BaseAttribute & {
  __typename: 'ComponentAttributesAttrPaymentType';
} => attribute?.__typename === 'ComponentAttributesAttrPaymentType';

const getPaymentBadges = (attribute?: BaseAttribute): PaymentBadgeSource[] =>
  (isSelectionAttribute(attribute)
    ? getPaymentBadgeSources(attribute)
    : []
  ).filter(
    (badge, index, badges) =>
      badges.findIndex((other) => other.label === badge.label) === index,
  );

const filterPaymentTypeBadges = ({
  brand,
  lang,
  badges,
}: {
  brand?: BrandId;
  lang?: string;
  badges: PaymentBadgeSource[];
}): PaymentBadgeSource[] =>
  badges.filter((badge) => {
    if (badge.label !== OCTOPUS_AUTO_ADD_VALUE) {
      return true;
    }

    return brand === 'moneyhero' && lang === 'zh-hk';
  });

export const mapProductHeroData = ({
  brand,
  bodyCopyOverride,
  headlineOverride,
  keyStats: keyStatsConfig,
  lang,
  product,
  productMetaConfig,
}: {
  brand?: BrandId;
  bodyCopyOverride?: string;
  headlineOverride?: string;
  keyStats?: { attribute?: SystemAttribute }[];
  lang?: string;
  product?: CmsProduct;
  productMetaConfig?: ComponentProductMetadata;
}): ProductHeroData => {
  const attributeIndex = createProductAttributeIndex(product);
  const headline =
    normalizeText(headlineOverride) ?? normalizeText(product?.product_name);
  const bodyCopy =
    normalizeText(bodyCopyOverride) ?? normalizeText(product?.description);
  const productTag = resolveProductTag({
    product,
    pageLevelTag: findMetadataProductCard(productMetaConfig, product)
      ?.productBlock?.text_tag,
  });
  const promoCode = getAttributeValue(
    getAttrFromProduct(ATTR_NAMES.PROMO_CODE, product),
  );

  const paymentNetworks = getPaymentBadges(
    getAttributeByName(attributeIndex, SUPPORTED_PAYMENT_NETWORK_ATTRIBUTE),
  );

  const paymentTypes = filterPaymentTypeBadges({
    brand,
    lang,
    badges: getPaymentBadges(
      getAttributeByName(attributeIndex, SUPPORTED_PAYMENT_TYPE_ATTRIBUTE),
    ),
  });

  const keyStats =
    keyStatsConfig
      ?.map((keyStat) => {
        const selector = keyStat.attribute;
        const attribute = getAttributeBySelector(attributeIndex, selector);
        const label = getAttributeLabel(attribute, selector);
        const value = getAttributeValue(attribute);

        if (!attribute || !value || !label) {
          return undefined;
        }

        return {
          key: selector?.uid ?? selector?.name ?? label,
          label,
          ...splitKeyStatValue(value),
        } satisfies ProductHeroKeyStat;
      })
      .filter(isDefined) ?? [];

  return {
    headline,
    bodyCopy,
    productTag,
    promoCode,
    paymentNetworks,
    paymentTypes,
    keyStats,
  };
};
